import { useEffect, useState, useCallback } from "react";
import { loadAcceptJs, tokenizeCard, validateCard } from "@/lib/acceptJs";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, CreditCard, Wallet, ShoppingCart, ArrowRight, Plus, X, UserPlus } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

let _k = 0;
const emptyPlayer = () => ({ key: ++_k, player_name: "", gender: "", dob: "", allergies: "", program_ids: [], saved_player_id: null, registered_program_ids: [] });

export default function Register() {
  usePageTitle("Register - Lansdowne Youth Rugby", "Register your child for Fall Rugby and other programs with Lansdowne Youth Rugby in Yonkers, New York. Sign up multiple players in one form.");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [config, setConfig] = useState(null);
  const [savedPlayers, setSavedPlayers] = useState([]);
  const [method, setMethod] = useState("cash");
  const [loading, setLoading] = useState(false);
  const [acceptReady, setAcceptReady] = useState(false);
  const [card, setCard] = useState({ cardNumber: "", month: "", year: "", cardCode: "", zip: "", fullName: "" });
  const [players, setPlayers] = useState([emptyPlayer()]);
  const [form, setForm] = useState({ guardian_name: "", guardian_email: "", guardian_phone: "", create_account: false, account_password: "", waiver_accepted: false });

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data));
    api.get("/payments/config").then((r) => { setConfig(r.data); setMethod(r.data.payment_configured ? "card" : "cash"); });
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, guardian_name: f.guardian_name || user.name || "", guardian_email: f.guardian_email || user.email || "" }));
      api.get("/my/players").then((r) => setSavedPlayers(r.data)).catch(() => {});
    }
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addPlayer = () => setPlayers((ps) => [...ps, emptyPlayer()]);
  const removePlayer = (key) => setPlayers((ps) => (ps.length > 1 ? ps.filter((p) => p.key !== key) : ps));
  const setPlayer = (key, patch) => setPlayers((ps) => ps.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  const toggleProgram = (key, pid) => setPlayers((ps) => ps.map((p) => {
    if (p.key !== key) return p;
    if (p.registered_program_ids?.includes(pid)) return p;
    const prog = programs.find((x) => x.id === pid);
    if (prog?.is_full) return p;
    const has = p.program_ids.includes(pid);
    return { ...p, program_ids: has ? p.program_ids.filter((x) => x !== pid) : [...p.program_ids, pid] };
  }));

  const addSavedPlayer = (sp) => {
    if (players.some((p) => p.saved_player_id === sp.id)) return toast.info(`${sp.player_name} is already added`);
    const blank = players.find((p) => !p.player_name && !p.saved_player_id && p.program_ids.length === 0);
    const filled = { key: blank ? blank.key : ++_k, player_name: sp.player_name, gender: sp.gender, dob: sp.dob, allergies: sp.allergies || "", program_ids: [], saved_player_id: sp.id, registered_program_ids: sp.registered_program_ids || [] };
    setPlayers((ps) => (blank ? ps.map((p) => (p.key === blank.key ? filled : p)) : [...ps, filled]));
  };

  // cart
  const lines = [];
  players.forEach((pl, idx) => pl.program_ids.forEach((pid) => {
    const prog = programs.find((p) => p.id === pid);
    if (prog) lines.push({ player: pl.player_name || `Player ${idx + 1}`, name: prog.name, price: prog.price_cents, key: `${pl.key}-${pid}` });
  }));
  const total = lines.reduce((s, l) => s + l.price, 0);

  // Load Accept.js when card payment is available so inline tokenization is ready.
  useEffect(() => {
    if (!config?.payment_configured) return;
    const src = config.environment === "production"
      ? "https://js.authorize.net/v1/Accept.js"
      : "https://jstest.authorize.net/v1/Accept.js";
    loadAcceptJs(src).then(() => setAcceptReady(true)).catch(() => setAcceptReady(false));
  }, [config]);

  const submit = useCallback(async () => {
    const active = players.filter((p) => p.program_ids.length > 0);
    if (active.length === 0) return toast.error("Select at least one program for a player");
    for (let i = 0; i < active.length; i++) {
      const p = active[i];
      if (!p.player_name) return toast.error(`Player ${i + 1}: name is required`);
      if (!p.gender) return toast.error(`Player ${i + 1}: Boy or Girl is required`);
      if (!p.dob) return toast.error(`Player ${i + 1}: date of birth is required`);
    }
    for (const [k, label] of [["guardian_name", "Guardian name"], ["guardian_email", "Guardian email"], ["guardian_phone", "Guardian phone"]]) {
      if (!form[k]) return toast.error(`${label} is required`);
    }
    if (form.create_account && !form.account_password) return toast.error("Set a password for your account");
    if (!form.waiver_accepted) return toast.error("Please read and accept the liability waiver to continue");

    const payByCard = total > 0 && method === "card" && config?.payment_configured;

    setLoading(true);
    try {
      let opaque = null;
      if (payByCard) {
        const cardErr = validateCard(card);
        if (cardErr) {
          setLoading(false);
          return toast.error(cardErr);
        }
        opaque = await tokenizeCard(
          { apiLoginID: config.api_login_id, clientKey: config.public_client_key },
          card
        );
      }
      const { data } = await api.post("/registrations", {
        players: active.map((p) => ({ player_name: p.player_name, gender: p.gender, dob: p.dob, allergies: p.allergies, program_ids: p.program_ids, saved_player_id: p.saved_player_id })),
        guardian_name: form.guardian_name, guardian_email: form.guardian_email, guardian_phone: form.guardian_phone,
        create_account: form.create_account, account_password: form.account_password,
        payment_method: method, opaque_data: opaque, waiver_accepted: form.waiver_accepted,
      });
      setCard({ cardNumber: "", month: "", year: "", cardCode: "", zip: "", fullName: "" });
      const n = data.count || 1;
      toast.success(data.payment_status === "cash_due" ? `Registered ${n} player${n > 1 ? "s" : ""}! Please bring cash to the field.` : `Registration complete for ${n} player${n > 1 ? "s" : ""} — see you on the pitch!`);
      navigate("/");
    } catch (err) {
      let msg;
      if (err?.response) {
        const detail = err.response.data?.detail;
        msg = detail == null
          ? `Payment could not be completed (error ${err.response.status}). Please check your card details or use Cash on Field.`
          : formatApiError(detail);
      } else {
        msg = err?.message || "Registration failed";
      }
      toast.error(msg);
    } finally { setLoading(false); }
  }, [players, form, total, method, config, card, navigate]);

  return (
    <div className="bg-brand-black min-h-screen text-white flex flex-col">
      <Navbar />
      <div className="max-w-4xl mx-auto w-full px-5 sm:px-8 py-16 flex-1">
        <div className="text-brand-gold text-xs font-bold tracking-[0.3em] uppercase mb-3">Fall 2026 & Beyond</div>
        <h1 className="font-display text-5xl sm:text-6xl mb-3">PLAYER REGISTRATION</h1>
        <p className="text-white/60 mb-12 max-w-xl">Add one or more players, pick programs for each, review your cart and register — all on one page.</p>

        {/* Saved children */}
        {user && savedPlayers.length > 0 && (
          <div className="border border-brand-gold/40 bg-brand-gold/5 p-5 mb-10" data-testid="saved-players">
            <div className="text-brand-gold text-xs font-bold tracking-[0.2em] uppercase mb-3">Your Children</div>
            <div className="flex flex-wrap gap-3">
              {savedPlayers.map((sp) => (
                <button key={sp.id} type="button" onClick={() => addSavedPlayer(sp)} className="inline-flex items-center gap-2 border border-white/20 hover:border-brand-gold px-4 py-2 text-sm transition-colors" data-testid={`saved-player-${sp.id}`}>
                  <UserPlus className="h-4 w-4 text-brand-gold" /> {sp.player_name}
                </button>
              ))}
            </div>
            <p className="text-white/40 text-xs mt-3">Tap a child to add them. Programs they're already registered for are marked REGISTERED.</p>
          </div>
        )}

        {/* Players */}
        <Section title="Players & Programs" step="01">
          <div className="space-y-6">
            {players.map((pl, i) => (
              <div key={pl.key} className="border border-white/10 p-5" data-testid={`player-block-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-display text-2xl tracking-wide text-brand-gold">
                    {pl.saved_player_id ? pl.player_name || `Player ${i + 1}` : `Player ${i + 1}`}
                  </span>
                  {players.length > 1 && (
                    <button type="button" onClick={() => removePlayer(pl.key)} className="text-white/50 hover:text-brand-red inline-flex items-center gap-1 text-sm" data-testid={`remove-player-${i}`}>
                      <X className="h-4 w-4" /> Remove
                    </button>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-white/80 mb-1.5 block">Player Name</Label>
                    <Input value={pl.player_name} onChange={(e) => setPlayer(pl.key, { player_name: e.target.value })} className="bg-brand-black border-white/15 rounded-none text-white" data-testid={`reg-player-name-${i}`} />
                  </div>
                  <div>
                    <Label className="text-white/80 mb-2 block">Boy or Girl</Label>
                    <RadioGroup value={pl.gender} onValueChange={(v) => setPlayer(pl.key, { gender: v })} className="flex gap-6 mt-3" data-testid={`reg-gender-${i}`}>
                      {["Boy", "Girl"].map((g) => (
                        <div key={g} className="flex items-center gap-2">
                          <RadioGroupItem value={g} id={`g-${pl.key}-${g}`} className="border-white/40 text-brand-red" />
                          <Label htmlFor={`g-${pl.key}-${g}`} className="text-white cursor-pointer">{g}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                  <div>
                    <Label className="text-white/80 mb-1.5 block">Date of Birth</Label>
                    <Input type="date" value={pl.dob} onChange={(e) => setPlayer(pl.key, { dob: e.target.value })} className="bg-brand-black border-white/15 rounded-none text-white" data-testid={`reg-dob-${i}`} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-white/80 mb-1.5 block">Please list any allergies</Label>
                    <Textarea value={pl.allergies} onChange={(e) => setPlayer(pl.key, { allergies: e.target.value })} placeholder="None" className="bg-brand-black border-white/15 rounded-none text-white" data-testid={`reg-allergies-${i}`} />
                  </div>
                </div>

                <div className="mt-5">
                  <Label className="text-white/80 mb-2 block">Programs for this player</Label>
                  <div className="space-y-2">
                    {programs.map((p) => {
                      const registered = pl.registered_program_ids?.includes(p.id);
                      const full = p.is_full && !registered;
                      const on = pl.program_ids.includes(p.id);
                      const locked = registered || full;
                      return (
                        <div key={p.id} role="button" tabIndex={0} aria-disabled={locked}
                          onClick={() => !locked && toggleProgram(pl.key, p.id)}
                          onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !locked) { e.preventDefault(); toggleProgram(pl.key, p.id); } }}
                          className={`w-full flex items-center justify-between p-4 border transition-colors ${registered ? "border-white/10 opacity-60 cursor-default" : full ? "border-white/10 opacity-50 cursor-not-allowed" : on ? "border-brand-red bg-brand-red/10 cursor-pointer" : "border-white/15 hover:border-white/40 cursor-pointer"}`}
                          data-testid={`reg-program-${i}-${p.id}`}>
                          <div className="flex items-center gap-3">
                            <span className={`h-5 w-5 flex items-center justify-center border ${on || registered ? "bg-brand-red border-brand-red" : "border-white/40"}`}>{(on || registered) && <Check className="h-4 w-4 text-white" />}</span>
                            <span className="font-display text-xl tracking-wide">{p.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            {registered && <Badge className="bg-green-600 rounded-none uppercase text-[10px] tracking-wider" data-testid={`registered-badge-${i}-${p.id}`}>Registered</Badge>}
                            {full && <Badge className="bg-white/20 rounded-none uppercase text-[10px] tracking-wider" data-testid={`full-badge-${i}-${p.id}`}>Full</Badge>}
                            <span className="font-display text-xl text-brand-gold">{money(p.price_cents)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" onClick={addPlayer} className="mt-5 rounded-none border-brand-gold text-brand-gold bg-transparent hover:bg-brand-gold hover:text-brand-black font-bold uppercase tracking-wider" data-testid="add-player-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Another Player
          </Button>
        </Section>

        {/* Guardian */}
        <Section title="Guardian & Contact" step="02">
          <p className="text-white/50 text-sm mb-5 -mt-3">One guardian for all players above.</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Guardian Name" testid="reg-guardian-name" value={form.guardian_name} onChange={(v) => set("guardian_name", v)} />
            <Field label="Guardian Email" type="email" testid="reg-guardian-email" value={form.guardian_email} onChange={(v) => set("guardian_email", v)} />
            <Field label="Guardian Phone" testid="reg-guardian-phone" value={form.guardian_phone} onChange={(v) => set("guardian_phone", v)} />
          </div>
        </Section>

        {/* Optional account */}
        {!user && (
          <div className="flex items-start gap-3 border border-white/10 p-5 mb-10">
            <Checkbox checked={form.create_account} onCheckedChange={(v) => set("create_account", !!v)} className="mt-1 border-white/40 data-[state=checked]:bg-brand-gold data-[state=checked]:border-brand-gold data-[state=checked]:text-brand-black" data-testid="reg-create-account" />
            <div className="flex-1">
              <div className="font-semibold">Create an account to save your children (optional)</div>
              <p className="text-sm text-white/50 mb-3">We'll remember your players so you can register them again next season in one tap. Not required.</p>
              {form.create_account && (
                <Input type="password" placeholder="Choose a password" value={form.account_password} onChange={(e) => set("account_password", e.target.value)} className="bg-brand-black border-white/15 rounded-none text-white max-w-xs" data-testid="reg-account-password" />
              )}
            </div>
          </div>
        )}

        {/* CART RECAP */}
        <div className="border-2 border-brand-red bg-white/[0.03] p-7 mb-8" data-testid="cart-recap">
          <div className="flex items-center gap-3 mb-5">
            <ShoppingCart className="h-6 w-6 text-brand-red" />
            <h3 className="font-display text-3xl tracking-wide">YOUR CART</h3>
          </div>
          {lines.length === 0 ? (
            <p className="text-white/50">No programs selected yet.</p>
          ) : (
            <div className="space-y-2">
              {lines.map((l, idx) => (
                <div key={l.key} className="flex justify-between text-sm border-b border-white/10 pb-2" data-testid={`cart-line-${idx}`}>
                  <span><span className="text-white/50">{l.player} · </span>{l.name}</span>
                  <span className="text-brand-gold font-semibold">{money(l.price)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-3 font-display text-3xl">
                <span>TOTAL</span><span className="text-brand-gold" data-testid="cart-total">{money(total)}</span>
              </div>
            </div>
          )}
        </div>

        {/* PAYMENT */}
        {total > 0 && (
          <Section title="Payment" step="03">
            <RadioGroup value={method} onValueChange={(v) => setMethod(v)} className="space-y-3" data-testid="reg-payment-method">
              {config?.payment_configured && (
                <label className={`flex items-center gap-3 p-4 border cursor-pointer ${method === "card" ? "border-brand-red" : "border-white/15"}`}>
                  <RadioGroupItem value="card" className="border-white/40 text-brand-red" data-testid="pay-card" />
                  <CreditCard className="h-5 w-5" /> <span>Pay by Card</span>
                </label>
              )}
              {config?.cash_on_field_enabled && (
                <label className={`flex items-center gap-3 p-4 border cursor-pointer ${method === "cash" ? "border-brand-red" : "border-white/15"}`}>
                  <RadioGroupItem value="cash" className="border-white/40 text-brand-red" data-testid="pay-cash" />
                  <Wallet className="h-5 w-5" /> <span>Cash on Field</span>
                </label>
              )}
            </RadioGroup>
            {method === "card" && config?.payment_configured && (
              <div className="mt-5 space-y-4 border border-white/10 p-5 bg-white/[0.02]" data-testid="reg-card-fields">
                <p className="text-xs uppercase tracking-widest text-brand-gold/90 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" /> Card Information
                </p>
                <div>
                  <Label className="text-white/80 mb-1.5 block">Card Number</Label>
                  <Input
                    inputMode="numeric" autoComplete="cc-number" placeholder="1234 5678 9012 3456"
                    value={card.cardNumber}
                    onChange={(e) => setCard((c) => ({ ...c, cardNumber: e.target.value }))}
                    className="bg-brand-black border-white/15 rounded-none text-white"
                    data-testid="card-number"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-white/80 mb-1.5 block">MM</Label>
                    <Input
                      inputMode="numeric" maxLength={2} autoComplete="cc-exp-month" placeholder="12"
                      value={card.month}
                      onChange={(e) => setCard((c) => ({ ...c, month: e.target.value }))}
                      className="bg-brand-black border-white/15 rounded-none text-white"
                      data-testid="card-month"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80 mb-1.5 block">YYYY</Label>
                    <Input
                      inputMode="numeric" maxLength={4} autoComplete="cc-exp-year" placeholder="2030"
                      value={card.year}
                      onChange={(e) => setCard((c) => ({ ...c, year: e.target.value }))}
                      className="bg-brand-black border-white/15 rounded-none text-white"
                      data-testid="card-year"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80 mb-1.5 block">CVV</Label>
                    <Input
                      inputMode="numeric" maxLength={4} autoComplete="cc-csc" placeholder="123"
                      value={card.cardCode}
                      onChange={(e) => setCard((c) => ({ ...c, cardCode: e.target.value }))}
                      className="bg-brand-black border-white/15 rounded-none text-white"
                      data-testid="card-cvv"
                    />
                  </div>
                </div>
                {!acceptReady && <p className="text-xs text-white/50">Loading secure card form…</p>}
                <div>
                  <Label className="text-white/80 mb-1.5 block">Cardholder Name</Label>
                  <Input
                    autoComplete="cc-name" placeholder="Name as it appears on card"
                    value={card.fullName}
                    onChange={(e) => setCard((c) => ({ ...c, fullName: e.target.value }))}
                    className="bg-brand-black border-white/15 rounded-none text-white"
                    data-testid="card-name"
                  />
                </div>
                <div>
                  <Label className="text-white/80 mb-1.5 block">Billing ZIP Code</Label>
                  <Input
                    inputMode="numeric" maxLength={10} autoComplete="postal-code" placeholder="10001"
                    value={card.zip}
                    onChange={(e) => setCard((c) => ({ ...c, zip: e.target.value }))}
                    className="bg-brand-black border-white/15 rounded-none text-white max-w-[200px]"
                    data-testid="card-zip"
                  />
                  <p className="mt-1.5 text-xs text-white/50">Must match the billing ZIP on file with your card.</p>
                </div>
              </div>
            )}
            {!config?.payment_configured && (
              <p className="mt-4 text-sm text-brand-gold/90 border border-brand-gold/30 p-3">Card payments are being set up. You can register now with Cash on Field.</p>
            )}
          </Section>
        )}

        <div className="border border-white/10 p-5 mb-8" data-testid="waiver-section">
          <h3 className="font-display text-xl tracking-wide mb-2 text-brand-gold">Liability Waiver & Photo Release</h3>
          <div className="text-xs text-white/60 leading-relaxed max-h-40 overflow-y-auto border border-white/10 p-3 mb-4 space-y-2" data-testid="waiver-text">
            <p>I acknowledge that participation in rugby and related activities organized by Lansdowne RFC New York ("the Club") involves inherent risks of physical injury. On behalf of my child and myself, I voluntarily assume all such risks.</p>
            <p>In consideration of my child being permitted to participate, I release, waive and hold harmless the Club, its coaches, volunteers, and affiliates from any and all claims, liabilities, or demands arising out of any injury, illness, or loss sustained during participation, to the fullest extent permitted by law.</p>
            <p>I confirm my child is medically fit to participate and I have disclosed any relevant allergies or conditions. In the event of a medical emergency, I authorize the Club to seek appropriate medical treatment.</p>
            <p>I grant the Club permission to use photographs and video of my child taken during Club activities for promotional and social media purposes, without compensation.</p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={form.waiver_accepted} onCheckedChange={(v) => set("waiver_accepted", !!v)} className="mt-0.5 border-white/40 data-[state=checked]:bg-brand-red data-[state=checked]:border-brand-red" data-testid="waiver-checkbox" />
            <span className="text-sm text-white/80">I have read and agree to the Liability Waiver & Photo Release on behalf of the player(s) above. <span className="text-brand-red">*</span></span>
          </label>
        </div>

        <Button onClick={submit} disabled={loading} className="w-full bg-brand-red hover:bg-brand-red/90 text-white rounded-none h-16 text-lg font-bold uppercase tracking-widest" data-testid="reg-submit">
          {loading ? "Processing…" : total > 0 ? `Register · ${money(total)}` : "Register"} <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
      <Footer />
    </div>
  );
}

function Section({ title, step, children }) {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-4 mb-6">
        <span className="font-display text-2xl text-brand-red">{step}</span>
        <h2 className="font-display text-3xl tracking-wide">{title}</h2>
        <span className="flex-1 h-px bg-white/10" />
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-white/80 mb-1.5 block">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="bg-brand-black border-white/15 rounded-none text-white" data-testid={testid} />
    </div>
  );
}
